import React, { useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Slider, createTheme, ThemeProvider, Tooltip } from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../../../sessionStorage';
import { EditableBasicInfoSummary } from "../../Common/SubComponents/BasicInfoSummary";
import QuoterTheme from '../../Common/Helpers/BasicInfoQuoterTheme';
import './BasicInfoSummaryExtended.scss';

export default function GetQuote(props) {
  var accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const sliderRef = useRef(null);

  const [value, setValue] = useState(0);

  const updateGhostPosition = () => {
    const ghostRef = sliderRef.current.querySelector(".MuiSlider-valueLabel");
    const thumbRef = sliderRef.current.querySelector(".MuiSlider-thumb");

    if (ghostRef && sliderRef.current) {
      const sliderRect = sliderRef.current.getBoundingClientRect();
      const ghostRect = ghostRef.getBoundingClientRect();
      const thumbRect = thumbRef.getBoundingClientRect();

      if ((Math.floor(thumbRect.left)) <= Math.floor(sliderRect.left) + (ghostRect.width / 2)) {
        if (!ghostRef.classList.contains("sticky-left")) {
          ghostRef.classList.add("sticky-left");
        }
      } else {
        if (ghostRef.classList.contains("sticky-left")) {
          ghostRef.classList.remove("sticky-left");
        }
      }

      if ((Math.floor(thumbRect.right)) >= Math.floor(sliderRect.right) - (ghostRect.width / 2)) {
        if (!ghostRef.classList.contains("sticky-right")) {
          ghostRef.classList.add("sticky-right");
        }
      } else {
        if (ghostRef.classList.contains("sticky-right")) {
          ghostRef.classList.remove("sticky-right");
        }
      }
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (props.isQuotingCoverageAmount) {
      props.setCoverageAmount(newValue);
    } else {
      props.setCoverageMonthlyRate(newValue);
    }
  };

  const handleQuoteOption = (option) => {
    props.setIsQuotingCoverageAmount(option);
    props.setLoading(true);
    if (props.isQuotingCoverageAmount) {
      props.setCoverageAmount(props.coverageAmountDefault);
    } else {
      props.setCoverageMonthlyRate(props.coverageMonthlyRateDefault);
    }
  };

  const handleToolTip = (e) => {
    e.stopPropagation();
  };

  const getFormattedCurrency = (value) => {
    return new Intl.NumberFormat("en-Us", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      updateGhostPosition();
    }, 0);
    return () => clearTimeout(timer);
  }, [value, props.isQuotingCoverageAmount, updateGhostPosition]);

  useEffect(() => {
    const handleResize = () => updateGhostPosition;
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    }
  }, [props.isQuotingCoverageAmount]);

  return (
    <div className="coverage-selector-container">
      <h2>Final Expense</h2>
      <EditableBasicInfoSummary
        gender={props.basicInfo.gender}
        state={props.basicInfo.state}
        birthdate={props.basicInfo.birthdate || props.basicInfo.fakeBirthdate}
        tobacco={props.basicInfo.tobacco}
        height={props.basicInfo.height}
        weight={props.basicInfo.weight}
        showEditLink={true}
        editLinkOnClick={() => { props.setOpenEditPopUp(true) }}
      />
      <ThemeProvider theme={() => createTheme({
        palette: {
          primary: {
            main: !!accountObj.account.primaryColor ? accountObj.account.primaryColor : "#3f51b5",
          },
        },
        ...QuoterTheme(),
        components: {
          MuiSlider: {
            styleOverrides: {
              root: {
                padding: "8px 0",
                "& .sticky-left": {
                  left: 0,
                  right: "auto",
                },
                "& .sticky-right": {
                  right: 0,
                  left: "auto",
                },
              },
              valueLabel: {
                fontSize: "1.75rem",
                fontWeight: 700,
                letterSpacing: "-1.8px",
                width: "150px",
                height: "50px",
                top: "78px",
                color: "#000",
                borderRadius: "5px",
                boxShadow: "0 4px 30px rgba(0,0,0,0.2)",
                backgroundColor: "#fff",
                "&::before": {
                  display: "none",
                },
                "& ::first-letter": {
                  fontSize: "12px",
                  letterSpacing: "1px",
                  verticalAlign: "super"
                },
              },
            },
          },
        },
      })}>
        <div className="quote-type-form">
          <div className="quote-type-container">
            <Row className='align-items-center pt-2 mx-0'>
              <Col xs={12} md={7} className={`px-0 quote-option-rounded`}>
                <Row className="quote-option-track px-3">
                  <button className={`quote-option left-radius pe-2 borderOutline ${props.isQuotingCoverageAmount ? "primaryBG" : 'disabled-class'}`} onClick={() => handleQuoteOption(true)}>
                    <div className="content-container">
                      <p className={`title`}>Coverage Amount</p>
                    </div>
                    <div className="help-container" onClick={handleToolTip}>
                      <Tooltip
                        title="Choose how much coverage you need. We'll show your monthly cost."
                        enterTouchDelay={0}
                        slotProps={{
                          tooltip: {
                            className: 'tooltip-font-bold',
                          },
                        }}
                      >
                        <span>
                          <InfoOutlinedIcon className="help-tooltip" />
                        </span>
                      </Tooltip>
                    </div>
                  </button>
                  <button className={`quote-option right-radius ps-2 borderOutline ${!props.isQuotingCoverageAmount ? "primaryBG" : 'disabled-class'}`} onClick={() => handleQuoteOption(false)}>
                    <div className="content-container">
                      <p className={`title`}>Monthly Rate</p>
                    </div>
                    <div className="help-container" onClick={handleToolTip}>
                      <Tooltip
                        title="Choose what you want to spend. We'll show your coverage."
                        enterTouchDelay={0}
                        slotProps={{
                          tooltip: {
                            className: 'tooltip-font-bold',
                          },
                        }}
                      >
                        <span>
                          <InfoOutlinedIcon className="help-tooltip" />
                        </span>
                      </Tooltip>
                    </div>
                  </button>
                </Row>
              </Col>
            </Row>
          </div>
          <div className='form-group'>
            <div className='select-slide-container'>
              <Slider
                min={(props.isQuotingCoverageAmount ? props.coverageAmountSpecs.minCoverageAmount : 10)}
                max={(props.isQuotingCoverageAmount ? props.coverageAmountSpecs.maxCoverageAmount : 200)}
                value={props.isQuotingCoverageAmount ? props.coverageAmount : props.coverageMonthlyRate}
                valueLabelFormat={getFormattedCurrency}
                step={props.isQuotingCoverageAmount ? 500 : 5}
                onChange={handleChange}
                className="m-2"
                marks={props.marks}
                valueLabelDisplay="on"
                ref={sliderRef}
              />
            </div>
          </div>
        </div>
      </ThemeProvider>
    </div>
  )
}
